// ✅ src/pages/Vehicle/MainVehicleTypeListing.jsx
// Combined page: form (create/edit) sits above the listing table.
// Visual design aligned with ProductionBOMListing.
//
// Backend mounts:
//   app.use("/api/mainTypeListing", mainVehicleTypeListing);
//   app.use("/api/mainTypeMaster", vehicleMainTypeMaster);
//
// Endpoints:
//   GET    /api/mainTypeListing?search=&page=1&limit=10
//   GET    /api/mainTypeMaster/:id
//   POST   /api/mainTypeMaster
//   PUT    /api/mainTypeMaster/:id
//
// Notes:
// - Sends localStorage values in headers:
//     x-finyearid, x-finyear, x-branchid, x-branch, x-userid
// - Clicking Vehicle Type / View opens SubType listing in new tab:
//     /vehicle/MainVehicleTypeSubTypeListing?vehicletypeid=ID
// - Edit populates the form on this same page (no route change required)

import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import HeaderBar from "../../components/HeaderBar";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";
import { useLocation, useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import VehicleLayoutWrapper from "../../components/VehicleLayoutWrapper";
import {
  FaPlus,
  FaSearch,
  FaChevronDown,
  FaChevronUp,
  FaSave,
} from "react-icons/fa";

const LIST_API = "https://nissi-india.com/api/mainTypeListing";
const MASTER_API = "https://nissi-india.com/api/mainTypeMaster";

const toNum = (v) => {
  const n = Number(String(v ?? "").trim());
  return Number.isFinite(n) ? n : 0;
};

const readQS = (search) => {
  const sp = new URLSearchParams(search);
  const obj = {};
  for (const [k, v] of sp.entries()) obj[k] = v;
  return obj;
};

const getLS = (key, fallback = "") => {
  if (typeof window === "undefined") return fallback;
  const val = localStorage.getItem(key);
  return val !== null && val !== undefined ? val : fallback;
};

const MainVehicleTypeListing = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;
  const tableHeaderClass = buttonClass;

  const formRef = useRef(null);
  const inputRef = useRef(null);

  const finYearId = getLS("finYearId");
  const finYear = getLS("finYear");
  const branchId = getLS("branchId") || getLS("branchid");
  const branch = getLS("branchType") || getLS("branchtype") || "";
  const userId =
    getLS("userId") || getLS("loginuserid") || getLS("loginUserId");

  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchId,
      "x-branch": branch,
      "x-branchtype": branch,
      "x-userid": userId,
    }),
    [finYearId, finYear, branchId, branch, userId]
  );

  const [showFilters, setShowFilters] = useState(true);

  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [err, setErr] = useState("");

  const [editId, setEditId] = useState(0);
  const [vehicleMainType, setVehicleMainType] = useState("");
  const [saving, setSaving] = useState(false);
  const [loadingEdit, setLoadingEdit] = useState(false);

  const qs = useMemo(() => readQS(location.search), [location.search]);

  const fieldClass = `w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
    isDark
      ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
      : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
  }`;

  const labelClass = `block mb-2 text-sm font-medium ${
    isDark ? "text-gray-300" : "text-gray-700"
  }`;

  const fetchData = async (customSearch = search, customPage = page) => {
    setLoading(true);
    setErr("");
    setHasSearched(true);

    try {
      const res = await axios.get(LIST_API, {
        params: {
          search: customSearch,
          page: customPage,
          limit,
        },
        headers: commonHeaders,
      });

      if (res.data?.success) {
        const data = res.data.data || [];
        setRows(data);
        setTotalPages(Number(res.data.totalPages || 1));
        if (data.length === 0) toast.info("No Records found");
      } else {
        setRows([]);
        setTotalPages(1);
        setErr(res.data?.message || "No records found");
      }
    } catch (e) {
      console.error("mainTypeListing fetch error:", e);
      setRows([]);
      setTotalPages(1);
      setErr("Failed to fetch vehicle types");
      toast.error(e?.response?.data?.message || "Failed to fetch vehicle types!");
    } finally {
      setLoading(false);
    }
  };

  const fetchEdit = async (id) => {
    if (!id) return;
    setLoadingEdit(true);
    try {
      const r = await axios.get(`${MASTER_API}/${id}`, {
        headers: commonHeaders,
      });
      const d = r?.data?.data;
      setVehicleMainType(
        String(d?.vehicleMainType ?? d?.vehicleType ?? d?.fld_vehicletypename ?? "").trim()
      );
    } catch (e) {
      console.error("Edit load error:", e);
      toast.error(e?.response?.data?.message || "Failed to load selected record!");
    } finally {
      setLoadingEdit(false);
    }
  };

  useEffect(() => {
    fetchData(search, page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, commonHeaders]);

  useEffect(() => {
    const idFromQs = toNum(qs.id || qs.mainTypeId || qs.vehicletypeid || 0);
    if (idFromQs) {
      setEditId(idFromQs);
      fetchEdit(idFromQs);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qs.id, qs.mainTypeId, qs.vehicletypeid, commonHeaders]);

  const scrollToForm = () => {
    formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    setTimeout(() => inputRef.current?.focus(), 250);
  };

  const clearForm = () => {
    setEditId(0);
    setVehicleMainType("");
    if (location.search) {
      navigate(location.pathname, { replace: true });
    }
  };

  const startAddNew = () => {
    clearForm();
    scrollToForm();
  };

  const startEdit = (row) => {
    const id = toNum(row?.id ?? row?.fld_vehicletypeid);
    if (!id) return;

    setEditId(id);
    setVehicleMainType(String(row?.vehicleType ?? row?.vehicleMainType ?? "").trim());
    scrollToForm();
    fetchEdit(id);
  };

  const handleSave = async () => {
    const name = String(vehicleMainType || "").trim();
    if (!name) {
      toast.error("Please enter a vehicle main type!");
      return;
    }

    setSaving(true);
    try {
      if (editId) {
        await axios.put(
          `${MASTER_API}/${editId}`,
          { vehicleMainType: name },
          { headers: commonHeaders }
        );
        toast.success("Vehicle Main Type updated successfully!");
      } else {
        await axios.post(
          MASTER_API,
          { vehicleMainType: name },
          { headers: commonHeaders }
        );
        toast.success("Vehicle Main Type saved successfully!");
      }

      clearForm();
      setPage(1);
      await fetchData(search, 1);
    } catch (error) {
      console.error("Save error:", error);
      toast.error(
        error?.response?.data?.message ||
          (editId ? "Failed to update vehicle main type!" : "Failed to save vehicle main type!")
      );
    } finally {
      setSaving(false);
    }
  };

  const handleSearch = () => {
    setPage(1);
    fetchData(search, 1);
  };

  const handleClearFilters = () => {
    setSearch("");
    setPage(1);
    fetchData("", 1);
  };

  const openSubType = (vehTypeId) => {
    if (!vehTypeId) return;
    window.open(`/vehicle/MainVehicleTypeSubTypeListing?vehicletypeid=${vehTypeId}`, "_blank");
  };

  return (
    <VehicleLayoutWrapper>
      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">VEHICLE MAIN TYPE LISTING</span>}
          setSkin={setSkin}
          currentSkin={currentSkin}
        />

        <div
          className={`p-6 mt-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : "bg-white text-black"
          }`}
        >
          {/* ==================== HEADER ==================== */}
          <div className="flex justify-between items-center mb-6 flex-wrap gap-3">
            <div className="flex items-center gap-3 flex-wrap">
              <h2 className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
                Vehicle Main Type Listing
              </h2>
              <span
                className={`px-3 py-1 rounded-full text-sm font-medium ${
                  isDark ? "bg-blue-900 text-white" : "bg-blue-100 text-blue-800"
                }`}
              >
                {loading ? "..." : `${rows.length}`} Records
              </span>
              {editId ? (
                <span
                  className={`px-3 py-1 rounded-full text-sm font-medium ${
                    isDark ? "bg-amber-900 text-amber-100" : "bg-amber-100 text-amber-800"
                  }`}
                >
                  Editing ID: {editId}
                </span>
              ) : null}
            </div>

            <div className="flex items-center gap-3 flex-wrap">
              

              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform transition duration-200 ${
                  isDark
                    ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600"
                    : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"
                }`}
              >
                {showFilters ? <FaChevronUp /> : <FaChevronDown />}
                {showFilters ? "Hide Filters" : "Show Filters"}
              </button>
            </div>
          </div>

          {/* ==================== ADD / EDIT FORM ==================== */}
          <div
            ref={formRef}
            className={`p-6 rounded-lg mb-6 ${isDark ? "bg-gray-800/50" : "bg-blue-50"}`}
          >
            <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
              <h3 className={`text-lg font-semibold ${isDark ? "text-white" : "text-blue-900"}`}>
                {editId ? "Edit Vehicle Main Type" : "Add Vehicle Main Type"}
              </h3>

              <div className="flex gap-2">
                {editId ? (
                  <button
                    type="button"
                    onClick={clearForm}
                    className={`px-4 py-2 rounded-lg text-sm font-semibold border ${
                      isDark
                        ? "border-gray-600 text-gray-200 hover:bg-gray-700"
                        : "border-gray-300 text-gray-700 hover:bg-white"
                    }`}
                  >
                    Cancel
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => setVehicleMainType("")}
                    className={`px-4 py-2 rounded-lg text-sm font-semibold border ${
                      isDark
                        ? "border-gray-600 text-gray-200 hover:bg-gray-700"
                        : "border-gray-300 text-gray-700 hover:bg-white"
                    }`}
                  >
                    Clear
                  </button>
                )}

                <button
                  type="button"
                  onClick={handleSave}
                  disabled={saving || !vehicleMainType.trim() || loadingEdit}
                  className={`flex items-center gap-2 px-6 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                    saving || !vehicleMainType.trim() || loadingEdit
                      ? "opacity-60 cursor-not-allowed bg-gray-400"
                      : `${buttonClass} ${hoverClass}`
                  } text-white`}
                >
                  <FaSave />
                  {saving ? "Saving..." : editId ? "Update" : "Save"}
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="vehicleMainType" className={labelClass}>
                  Vehicle Main Type
                </label>
                <input
                  ref={inputRef}
                  id="vehicleMainType"
                  type="text"
                  value={vehicleMainType}
                  onChange={(e) => setVehicleMainType(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handleSave();
                  }}
                  placeholder={loadingEdit ? "Loading..." : "Enter vehicle main type..."}
                  disabled={loadingEdit}
                  className={fieldClass}
                />
                <div className={`text-xs mt-2 ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                  Duplicate names are not allowed (same as ASPX).
                </div>
              </div>
            </div>
          </div>

          {/* ==================== SEARCH FILTERS ==================== */}
          {showFilters && (
            <div className={`p-6 rounded-lg mb-6 ${isDark ? "bg-gray-800/50" : "bg-blue-50"}`}>
              <div className="flex items-center justify-between mb-4">
                <h3 className={`text-lg font-semibold ${isDark ? "text-white" : "text-blue-900"}`}>
                  Search Criteria
                </h3>

                <div className="flex gap-2">
                  <button
                    onClick={handleClearFilters}
                    className={`px-4 py-2 rounded-lg text-sm font-semibold border ${
                      isDark
                        ? "border-gray-600 text-gray-200 hover:bg-gray-700"
                        : "border-gray-300 text-gray-700 hover:bg-white"
                    }`}
                  >
                    Clear
                  </button>

                  <button
                    onClick={handleSearch}
                    disabled={loading}
                    className={`flex items-center gap-2 px-6 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                      loading ? "opacity-60 cursor-not-allowed" : ""
                    } ${buttonClass} ${hoverClass} text-white`}
                  >
                    <FaSearch />
                    {loading ? "Searching..." : "Search"}
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className={labelClass}>Search Vehicle Types</label>
                  <input
                    type="text"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") handleSearch();
                    }}
                    placeholder="Type to search vehicle types..."
                    className={fieldClass}
                  />
                </div>
              </div>
            </div>
          )}

          {/* ==================== RESULTS TABLE ==================== */}
          <div className="overflow-x-auto rounded-lg border shadow-lg">
            {loading ? (
              <div className={`p-16 text-center ${isDark ? "bg-gray-800" : "bg-gray-50"}`}>
                <div className={`text-lg font-medium ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                  Loading...
                </div>
              </div>
            ) : rows.length === 0 ? (
              <div className={`p-16 text-center ${isDark ? "bg-gray-800" : "bg-gray-50"}`}>
                <div className={`text-lg font-medium ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                  {err || (hasSearched ? "No Records found" : "Please search to view records")}
                </div>
              </div>
            ) : (
              <table className={`w-full ${isDark ? "bg-gray-800" : "bg-white"}`}>
                <thead className={`${tableHeaderClass} text-white`}>
                  <tr>
                    <th className="px-6 py-4 text-left text-sm font-semibold">S.No</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold">Vehicle Type</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold">Index</th>
                    <th className="px-6 py-4 text-center text-sm font-semibold">Edit</th>
                    <th className="px-6 py-4 text-center text-sm font-semibold">View</th>
                  </tr>
                </thead>

                <tbody className={`${isDark ? "divide-gray-700" : "divide-gray-200"} divide-y`}>
                  {rows.map((row, idx) => {
                    const rowId = toNum(row.id ?? row.fld_vehicletypeid);
                    const isEditingRow = editId && rowId === editId;

                    return (
                      <tr
                        key={row.id ?? idx}
                        className={`${
                          isEditingRow
                            ? isDark
                              ? "bg-amber-900/30"
                              : "bg-amber-50"
                            : idx % 2 === 0
                            ? isDark
                              ? "bg-gray-800"
                              : "bg-white"
                            : isDark
                            ? "bg-gray-750"
                            : "bg-gray-50"
                        } transition-colors ${isDark ? "hover:bg-gray-700" : "hover:bg-blue-50"}`}
                      >
                        <td className={`px-6 py-4 text-sm ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {(page - 1) * limit + idx + 1}
                        </td>

                        <td className="px-6 py-4 text-sm">
                          <button
                            type="button"
                            onClick={() => openSubType(row.id)}
                            className={`font-medium underline text-left ${
                              isDark ? "text-blue-400 hover:text-blue-300" : "text-blue-600 hover:text-blue-800"
                            }`}
                            title="Open subtype listing"
                          >
                            {row.vehicleType || "-"}
                          </button>
                        </td>

                        <td className={`px-6 py-4 text-sm font-medium ${isDark ? "text-white" : "text-gray-900"}`}>
                          {row.index ?? "-"}
                        </td>

                        <td className="px-6 py-4 text-center">
                          <button
                            type="button"
                            onClick={() => startEdit(row)}
                            className="text-yellow-500 hover:text-yellow-600 text-lg transition"
                            title="Edit"
                          >
                            ✏️
                          </button>
                        </td>

                        <td className="px-6 py-4 text-center">
                          <button
                            type="button"
                            onClick={() => openSubType(row.id)}
                            className={`text-sm font-semibold ${
                              isDark ? "text-green-400 hover:text-green-300" : "text-green-600 hover:text-green-700"
                            }`}
                            title="View subtypes"
                          >
                            👁
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>

          {/* ==================== PAGINATION ==================== */}
          {!loading && totalPages > 1 && (
            <div className="flex flex-col md:flex-row items-center justify-between mt-6 gap-4">
              <div className={`text-sm ${isDark ? "text-white" : "text-gray-700"}`}>
                Page <span className="font-semibold">{page}</span> of{" "}
                <span className="font-semibold">{totalPages}</span>
              </div>

              <div className="inline-flex rounded-lg shadow-sm overflow-hidden border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900">
                <button
                  onClick={() => setPage((p) => Math.max(p - 1, 1))}
                  disabled={page === 1}
                  className={`px-4 py-2 text-sm font-medium ${
                    page === 1
                      ? "text-gray-400 cursor-not-allowed"
                      : isDark
                      ? "text-white"
                      : "text-gray-700"
                  }`}
                >
                  Prev
                </button>

                {Array.from({ length: totalPages }).map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setPage(i + 1)}
                    className={`px-4 py-2 text-sm font-medium transition ${
                      page === i + 1
                        ? `${buttonClass} text-white`
                        : isDark
                        ? "text-white"
                        : "text-gray-700"
                    }`}
                  >
                    {i + 1}
                  </button>
                ))}

                <button
                  onClick={() => setPage((p) => Math.min(p + 1, totalPages))}
                  disabled={page === totalPages}
                  className={`px-4 py-2 text-sm font-medium ${
                    page === totalPages
                      ? "text-gray-400 cursor-not-allowed"
                      : isDark
                      ? "text-white"
                      : "text-gray-700"
                  }`}
                >
                  Next
                </button>
              </div>
            </div>
          )}

          <ToastContainer />
        </div>
      </div>
    </VehicleLayoutWrapper>
  );
};

export default MainVehicleTypeListing;